// ═══════════════════════════════════════════════════════════
// Data Storage Property Key Maps
// Commission Journal — snake_case ↔ camelCase
// ═══════════════════════════════════════════════════════════

/**
 * Commission Journal (Phase 4+): snapshot-only B24 properties.
 * New installs and all new entries use only these 7 keys.
 * Storage key (snake_case) → App field (camelCase)
 */
export const JOURNAL_STORAGE_MAP = {
  d1_jnl_id: "d1JournalId",
  formula_snap: "formulaSnapshot",
  input_snap: "inputSnapshot",
  rep_snap: "repSnapshot",
  product_snap: "productSnapshot",
  approval_snap: "approvalSnapshot",
  fx_snap: "fxSnapshot",
} as const;

/** Commission Journal (Phase 4+): App field → Storage key */
export const JOURNAL_APP_MAP = Object.fromEntries(
  Object.entries(JOURNAL_STORAGE_MAP).map(([k, v]) => [v, k]),
) as Record<string, string>;

/**
 * LEGACY Commission Journal (pre-Phase 2b): all 32 scalar + snapshot properties.
 * Used by data-storage.service.ts when reading old B24 journal entries for
 * migration tasks, snapshot generation, or recalculation.
 * Storage key (snake_case) → App field (camelCase)
 */
export const LEGACY_JOURNAL_STORAGE_MAP = {
  entity_id: "entityId",
  entity_type_id: "entityTypeId",
  rep_id: "repId",
  rule_id: "ruleId",
  rule_version: "ruleVersion",
  period: "period",
  status: "status",
  cmn_type: "commissionType",
  cmn_structure: "commissionStructure",
  calc_basis: "calculationBasis",
  base_amount: "baseAmount",
  rate: "rate",
  calc_amount: "calculatedAmount",
  currency: "currency",
  orig_amount: "originalAmount",
  conv_amount: "convertedAmount",
  original_id: "originalId",
  split_group: "splitGroup",
  split_pct: "splitPercent",
  formula_snap: "formulaSnapshot",
  input_snap: "inputSnapshot",
  rep_snap: "repSnapshot",
  approval_snap: "approvalSnapshot",
  product_snap: "productSnapshot",
  tier_detail: "tierDetail",
  cap_applied: "capApplied",
  base_plus_amt: "basePlusAmount",
  paid_at: "paidAt",
  notes: "notes",
  created_at: "createdAt",
  fx_snap: "fxSnapshot",
  d1_jnl_id: "d1JournalId",
} as const;

/** LEGACY Commission Journal: App field → Storage key */
export const LEGACY_JOURNAL_APP_MAP = Object.fromEntries(
  Object.entries(LEGACY_JOURNAL_STORAGE_MAP).map(([k, v]) => [v, k]),
) as Record<string, string>;

/**
 * @deprecated Phase 1+. Rule metadata moved to D1 rules table.
 * Kept for data-storage.service.ts read path (fetchAllRules used by migration task).
 * Storage key (snake_case) → App field (camelCase)
 */
export const RULE_STORAGE_MAP = {
  rule_name: "ruleName",
  version: "version",
  is_active: "isActive",
  structure: "structure",
  cmn_type: "commissionType",
  calc_basis: "calculationBasis",
  currency: "currency",
  triggers: "triggers",
  conditions: "conditions",
  formula: "formula",
  tiers: "tiers",
  caps: "caps",
  base_plus_config: "basePlusConfig",
  split_config: "splitConfig",
  clawback_config: "clawbackConfig",
  spiff_config: "spiffConfig",
  actions: "actionsConfig",
  eff_from: "effectiveFrom",
  eff_to: "effectiveTo",
  priority: "priority",
  created_by: "createdBy",
  prev_ver_id: "previousVersionId",
  notes: "notes",
  basis_fref: "basisFieldRef",
  tier_acc_ref: "tierAccumulatorRef",
  def_rep_res: "defaultRepResolver",
  prd_rate_cfg: "productRateConfig",
} as const;

/**
 * @deprecated Phase 1+. Use D1 rules table.
 * Rule Metadata: App field (camelCase) → Storage key (snake_case)
 */
export const RULE_APP_MAP = Object.fromEntries(
  Object.entries(RULE_STORAGE_MAP).map(([k, v]) => [v, k]),
) as Record<string, string>;

/** Properties that store JSON and need parse/stringify */
const JSON_PROPERTIES = new Set([
  "triggers",
  "conditions",
  "formula",
  "tiers",
  "caps",
  "base_plus_config",
  "split_config",
  "clawback_config",
  "spiff_config",
  "actions",
  "formula_snap",
  "input_snap",
  "rep_snap",
  "approval_snap",
  "product_snap",
  "tier_detail",
  "basis_fref",
  "tier_acc_ref",
  "def_rep_res",
  "prd_rate_cfg",
  "fx_snap",
]);

/**
 * Convert a camelCase app object → snake_case PROPERTY_VALUES for Data Storage write.
 * JSON-type properties are automatically stringified.
 */
export function toStorageProps(
  appObj: Record<string, unknown>,
  fieldMap: Record<string, string>,
): Record<string, string> {
  const result: Record<string, string> = {};

  for (const [camelKey, value] of Object.entries(appObj)) {
    const storageKey = fieldMap[camelKey];
    if (!storageKey || value === undefined || value === null) continue;

    if (JSON_PROPERTIES.has(storageKey) && typeof value === "object") {
      result[storageKey] = JSON.stringify(value);
    } else {
      result[storageKey] = String(value);
    }
  }

  return result;
}

/**
 * Convert snake_case PROPERTY_VALUES from Data Storage → camelCase app object.
 * JSON-type properties are automatically parsed.
 */
export function fromStorageProps(
  storageObj: Record<string, string>,
  fieldMap: Record<string, string>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [storageKey, value] of Object.entries(storageObj)) {
    const camelKey = fieldMap[storageKey];
    if (!camelKey) continue;

    if (JSON_PROPERTIES.has(storageKey) && value) {
      try {
        result[camelKey] = JSON.parse(value);
      } catch {
        result[camelKey] = value;
      }
    } else {
      result[camelKey] = value;
    }
  }

  return result;
}

// ═══════════════════════════════════════════════════════════
// Data Storage Property Definition Arrays
// Used during provisioning (entity.item.property.add)
// ═══════════════════════════════════════════════════════════

/**
 * Phase 4+: cmn_journal is a snapshot-only store — 7 properties.
 * New portals and all new entries register/use only these keys.
 */
export const JOURNAL_PROPERTIES = [
  { PROPERTY: "d1_jnl_id", NAME: "D1 Journal ID", TYPE: "S" },
  { PROPERTY: "formula_snap", NAME: "Formula Snapshot", TYPE: "S" },
  { PROPERTY: "input_snap", NAME: "Input Data Snapshot", TYPE: "S" },
  { PROPERTY: "rep_snap", NAME: "Rep Snapshot", TYPE: "S" },
  { PROPERTY: "product_snap", NAME: "Product Snapshot", TYPE: "S" },
  { PROPERTY: "approval_snap", NAME: "Approval History", TYPE: "S" },
  { PROPERTY: "fx_snap", NAME: "FX Rate Snapshot", TYPE: "S" },
] as const;

/**
 * LEGACY: all 32 journal properties registered before Phase 2b.
 * Kept for backward-compatibility checks and migration tooling only.
 */
export const LEGACY_JOURNAL_PROPERTIES = [
  { PROPERTY: "entity_id", NAME: "Source Entity ID", TYPE: "S" },
  { PROPERTY: "entity_type_id", NAME: "Source Entity Type ID", TYPE: "S" },
  { PROPERTY: "rep_id", NAME: "Sales Rep", TYPE: "S" },
  { PROPERTY: "rule_id", NAME: "Rule ID", TYPE: "S" },
  { PROPERTY: "rule_version", NAME: "Rule Version", TYPE: "S" },
  { PROPERTY: "period", NAME: "Fiscal Period", TYPE: "S" },
  { PROPERTY: "status", NAME: "Journal Status", TYPE: "S" },
  { PROPERTY: "cmn_type", NAME: "Commission Type", TYPE: "S" },
  { PROPERTY: "cmn_structure", NAME: "Commission Structure", TYPE: "S" },
  { PROPERTY: "calc_basis", NAME: "Calculation Basis", TYPE: "S" },
  { PROPERTY: "base_amount", NAME: "Base Amount", TYPE: "S" },
  { PROPERTY: "rate", NAME: "Applied Rate (%)", TYPE: "S" },
  { PROPERTY: "calc_amount", NAME: "Calculated Amount", TYPE: "S" },
  { PROPERTY: "currency", NAME: "Currency", TYPE: "S" },
  { PROPERTY: "orig_amount", NAME: "Original Amount", TYPE: "S" },
  { PROPERTY: "conv_amount", NAME: "Converted Amount", TYPE: "S" },
  { PROPERTY: "original_id", NAME: "Original Entry ID", TYPE: "S" },
  { PROPERTY: "split_group", NAME: "Split Group ID", TYPE: "S" },
  { PROPERTY: "split_pct", NAME: "Split Percentage", TYPE: "S" },
  { PROPERTY: "formula_snap", NAME: "Formula Snapshot", TYPE: "S" },
  { PROPERTY: "input_snap", NAME: "Input Data Snapshot", TYPE: "S" },
  { PROPERTY: "rep_snap", NAME: "Rep Snapshot", TYPE: "S" },
  { PROPERTY: "approval_snap", NAME: "Approval History", TYPE: "S" },
  { PROPERTY: "product_snap", NAME: "Product Snapshot", TYPE: "S" },
  { PROPERTY: "tier_detail", NAME: "Tier Detail", TYPE: "S" },
  { PROPERTY: "cap_applied", NAME: "Cap Applied", TYPE: "S" },
  { PROPERTY: "base_plus_amt", NAME: "Base Plus Amount", TYPE: "S" },
  { PROPERTY: "paid_at", NAME: "Paid At", TYPE: "S" },
  { PROPERTY: "notes", NAME: "Notes", TYPE: "S" },
  { PROPERTY: "created_at", NAME: "Created At", TYPE: "S" },
  { PROPERTY: "fx_snap", NAME: "FX Rate Snapshot", TYPE: "S" },
  { PROPERTY: "d1_jnl_id", NAME: "D1 Journal ID", TYPE: "S" },
] as const;

/**
 * @deprecated Phase 1+. Rule metadata moved to D1 rules table.
 * Kept only for the MIGRATE_B24_RULES_TO_D1 one-time task and legacy tests.
 */
export const RULE_PROPERTIES = [
  { PROPERTY: "rule_name", NAME: "Rule Name", TYPE: "S" },
  { PROPERTY: "version", NAME: "Version", TYPE: "S" },
  { PROPERTY: "is_active", NAME: "Active?", TYPE: "S" },
  { PROPERTY: "structure", NAME: "Commission Structure", TYPE: "S" },
  { PROPERTY: "cmn_type", NAME: "Commission Type", TYPE: "S" },
  { PROPERTY: "calc_basis", NAME: "Calculation Basis", TYPE: "S" },
  { PROPERTY: "currency", NAME: "Currency", TYPE: "S" },
  { PROPERTY: "triggers", NAME: "Trigger Config", TYPE: "S" },
  { PROPERTY: "conditions", NAME: "Condition Config", TYPE: "S" },
  { PROPERTY: "formula", NAME: "Formula Config", TYPE: "S" },
  { PROPERTY: "tiers", NAME: "Tier Config", TYPE: "S" },
  { PROPERTY: "caps", NAME: "Cap Config", TYPE: "S" },
  { PROPERTY: "base_plus_config", NAME: "Base-Plus Config", TYPE: "S" },
  { PROPERTY: "split_config", NAME: "Split Config", TYPE: "S" },
  { PROPERTY: "clawback_config", NAME: "Clawback Config", TYPE: "S" },
  { PROPERTY: "spiff_config", NAME: "SPIFF Config", TYPE: "S" },
  { PROPERTY: "actions", NAME: "Actions Config", TYPE: "S" },
  { PROPERTY: "eff_from", NAME: "Effective Start", TYPE: "S" },
  { PROPERTY: "eff_to", NAME: "Effective End", TYPE: "S" },
  { PROPERTY: "priority", NAME: "Priority", TYPE: "S" },
  { PROPERTY: "created_by", NAME: "Created By", TYPE: "S" },
  { PROPERTY: "prev_ver_id", NAME: "Previous Version ID", TYPE: "S" },
  { PROPERTY: "notes", NAME: "Notes", TYPE: "S" },
  { PROPERTY: "basis_fref", NAME: "Basis Field Ref", TYPE: "S" },
  { PROPERTY: "tier_acc_ref", NAME: "Tier Accumulator Ref", TYPE: "S" },
  { PROPERTY: "def_rep_res", NAME: "Default Rep Resolver", TYPE: "S" },
  { PROPERTY: "prd_rate_cfg", NAME: "Product Rate Config", TYPE: "S" },
] as const;

// ═══════════════════════════════════════════════════════════
// Metric Key Type (used by D1 SQL aggregation in metrics.service.ts)
// ═══════════════════════════════════════════════════════════
export type MetricKey =
  | "REV"
  | "CNT"
  | "COM"
  | "COM_PAID"
  | "COM_PENDING"
  | "COM_APPROVED"
  | "COM_DISPUTED"
  | "COM_VOID"
  | "COM_CLAWBACK"
  | "CNT_PENDING"
  | "CNT_APPROVED"
  | "CNT_PAID"
  | "CNT_DISPUTED"
  | "CNT_VOID"
  | "CNT_CLAWBACK"
  | `CF:${string}`;
